import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, Play, Maximize, Minimize, RotateCw, AlertCircle } from 'lucide-react';
import * as api from '../services/api';

const MiniAppHomePage = ({ onVideoPlayingChange }) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [loadedBannerIds, setLoadedBannerIds] = useState([]);
  const [failedBannerIds, setFailedBannerIds] = useState([]);
  const [isBannerAutoPaused, setIsBannerAutoPaused] = useState(false);
  const [bannerDragX, setBannerDragX] = useState(0);
  const [isBannerGestureActive, setIsBannerGestureActive] = useState(false);
  const [isBannerSettling, setIsBannerSettling] = useState(false);
  const [pressedCategoryId, setPressedCategoryId] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null); // 当前播放的视频
  const [isLoading, setIsLoading] = useState(true); // 加载状态
  
  // 监听视频播放状态变化，通知父组件
  useEffect(() => {
    if (onVideoPlayingChange) {
      onVideoPlayingChange(!!playingVideo);
    }
  }, [playingVideo, onVideoPlayingChange]);
  const [isFullscreen, setIsFullscreen] = useState(false); // 全屏状态
  const [orientation, setOrientation] = useState('portrait'); // 横竖屏状态
  const [error, setError] = useState(null); // 错误状态
  const videoRef = useRef(null);
  const bannerViewportRef = useRef(null);
  const bannerTouchStartRef = useRef(null);
  const bannerDragXRef = useRef(0);
  const bannerResumeTimerRef = useRef(null);
  const bannerSettleTimerRef = useRef(null);
  const categoryNavigationTimerRef = useRef(null);

  // 从API获取分类数据
  const [videoCategories, setVideoCategories] = useState([]);

  // API数据状态（Banner和精选视频）
  const [bannerData, setBannerData] = useState([]);
  const [bottomBanner, setBottomBanner] = useState(null);
  const [featuredVideos, setFeaturedVideos] = useState([]);


  // 滚动到指定页面
  const scrollToSection = (index) => {
    if (containerRef.current) {
      const windowHeight = containerRef.current.clientHeight;
      containerRef.current.scrollTo({
        top: windowHeight * index,
        behavior: 'smooth'
      });
    }
  };

  const visibleBanners = bannerData.filter((banner) => (
    loadedBannerIds.includes(banner.id) && !failedBannerIds.includes(banner.id)
  ));

  const markBannerLoaded = (id) => {
    setLoadedBannerIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  };

  const markBannerFailed = (id) => {
    setFailedBannerIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  };

  useEffect(() => {
    setCurrentBanner((index) => Math.max(0, Math.min(index, visibleBanners.length - 1)));
  }, [visibleBanners.length]);

  const moveBanner = (direction) => {
    if (visibleBanners.length < 2) return;
    setCurrentBanner((index) => (
      (index + direction + visibleBanners.length) % visibleBanners.length
    ));
  };

  const clearBannerResumeTimer = () => {
    if (bannerResumeTimerRef.current) {
      clearTimeout(bannerResumeTimerRef.current);
      bannerResumeTimerRef.current = null;
    }
  };

  const pauseBannerAutoPlay = () => {
    setIsBannerAutoPaused(true);
    clearBannerResumeTimer();
  };

  // 用户完成滑动后，静止 4 秒才恢复原有的淡入淡出自动轮播。
  const resumeBannerAutoPlayAfterDelay = () => {
    clearBannerResumeTimer();
    bannerResumeTimerRef.current = setTimeout(() => {
      setIsBannerAutoPaused(false);
      setCurrentBanner((index) => (
        visibleBanners.length > 1 ? (index + 1) % visibleBanners.length : index
      ));
      bannerResumeTimerRef.current = null;
    }, 4000);
  };

  useEffect(() => () => {
    if (bannerResumeTimerRef.current) clearTimeout(bannerResumeTimerRef.current);
    if (bannerSettleTimerRef.current) clearTimeout(bannerSettleTimerRef.current);
    if (categoryNavigationTimerRef.current) clearTimeout(categoryNavigationTimerRef.current);
  }, []);

  const handleBannerTouchStart = (event) => {
    if (isBannerSettling || visibleBanners.length < 2) return;
    pauseBannerAutoPlay();
    const touch = event.touches?.[0];
    if (!touch) return;
    bannerDragXRef.current = 0;
    setBannerDragX(0);
    setIsBannerGestureActive(true);
    bannerTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      lastX: touch.clientX,
      lastTime: performance.now(),
      velocityX: 0,
      axis: null,
      width: bannerViewportRef.current?.clientWidth || window.innerWidth
    };
  };

  const handleBannerTouchMove = (event) => {
    const start = bannerTouchStartRef.current;
    const touch = event.touches?.[0];
    if (!start || !touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (!start.axis) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 6) return;
      start.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      if (start.axis === 'y') {
        setIsBannerGestureActive(false);
        return;
      }
    }
    if (start.axis !== 'x') return;

    event.preventDefault();
    const now = performance.now();
    const elapsed = Math.max(1, now - start.lastTime);
    start.velocityX = (touch.clientX - start.lastX) / elapsed;
    start.lastX = touch.clientX;
    start.lastTime = now;

    const nextDragX = Math.max(-start.width, Math.min(start.width, deltaX));
    bannerDragXRef.current = nextDragX;
    setBannerDragX(nextDragX);
  };

  const settleBannerSwipe = (direction = 0) => {
    const start = bannerTouchStartRef.current;
    const width = start?.width || bannerViewportRef.current?.clientWidth || window.innerWidth;
    setIsBannerSettling(true);
    setIsBannerGestureActive(true);

    const targetX = direction === 0 ? 0 : (direction > 0 ? -width : width);
    bannerDragXRef.current = targetX;
    setBannerDragX(targetX);

    bannerSettleTimerRef.current = setTimeout(() => {
      if (direction !== 0) moveBanner(direction);
      bannerDragXRef.current = 0;
      setBannerDragX(0);
      setIsBannerSettling(false);
      setIsBannerGestureActive(false);
      bannerSettleTimerRef.current = null;
    }, 280);
  };

  const handleBannerTouchEnd = (event) => {
    const start = bannerTouchStartRef.current;
    const touch = event.changedTouches?.[0];
    if (!start) return;

    const deltaX = touch ? touch.clientX - start.x : bannerDragXRef.current;
    const threshold = Math.min(64, start.width * 0.14);
    const shouldChange = start.axis === 'x' && (
      Math.abs(deltaX) >= threshold || Math.abs(start.velocityX) >= 0.45
    );
    const direction = shouldChange ? (deltaX < 0 ? 1 : -1) : 0;

    settleBannerSwipe(direction);
    bannerTouchStartRef.current = null;
    resumeBannerAutoPlayAfterDelay();
  };

  const handleBannerTouchCancel = () => {
    if (!bannerTouchStartRef.current) return;
    settleBannerSwipe(0);
    bannerTouchStartRef.current = null;
    resumeBannerAutoPlayAfterDelay();
  };

  const getBannerRelativePosition = (index) => {
    const count = visibleBanners.length;
    if (count < 2) return index - currentBanner;
    if (count === 2 && index !== currentBanner) {
      return bannerDragX >= 0 ? -1 : 1;
    }

    let distance = index - currentBanner;
    if (distance > count / 2) distance -= count;
    if (distance < -count / 2) distance += count;
    return distance;
  };

  const handleCategoryClick = (categoryId) => {
    if (categoryNavigationTimerRef.current) {
      clearTimeout(categoryNavigationTimerRef.current);
    }
    setPressedCategoryId(categoryId);
    categoryNavigationTimerRef.current = setTimeout(() => {
      setPressedCategoryId(null);
      navigate(`/category/${categoryId}`);
      categoryNavigationTimerRef.current = null;
    }, 280);
  };

  // 获取首页数据
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const homeData = await api.getHomeData();

        setVideoCategories((homeData.categories || []).map(category => ({
          ...category,
          id: category.id || category.slug || category._id,
          title: category.title || category.name,
          image: api.getMediaUrl(category.image || category.homeCard?.image)
        })));
        setBannerData((homeData.banners || []).map(banner => ({
          ...banner,
          id: banner.id || banner._id,
          image: api.getMediaUrl(banner.image)
        })));
        setBottomBanner(homeData.bottomBanner ? {
          ...homeData.bottomBanner,
          image: api.getMediaUrl(homeData.bottomBanner.image)
        } : null);
        setFeaturedVideos((homeData.featuredVideos || []).map(video => ({
          ...video,
          id: video.id || video._id,
          cover: api.getMediaUrl(video.cover),
          videoUrl: api.getMediaUrl(video.videoUrl)
        })));
      } catch (err) {
        console.error('❌ 获取首页数据失败:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHomeData();
  }, []);

  // Banner自动轮播
  useEffect(() => {
    if (visibleBanners.length < 2 || isBannerAutoPaused) return;

    const timer = setInterval(() => {
      setCurrentBanner((index) => (index + 1) % visibleBanners.length);
    }, 3000); // 每3秒切换

    return () => clearInterval(timer);
  }, [visibleBanners.length, isBannerAutoPaused]);

  // 设备旋转检测
  useEffect(() => {
    const handleOrientationChange = () => {
      const isLandscape = window.orientation === 90 || window.orientation === -90;
      setOrientation(isLandscape ? 'landscape' : 'portrait');
      
      // 如果正在播放视频，自动调整全屏状态
      if (playingVideo && videoRef.current) {
        if (isLandscape && !isFullscreen) {
          // 横屏时自动全屏
          enterFullscreen();
        } else if (!isLandscape && isFullscreen) {
          // 竖屏时退出全屏
          exitFullscreen();
        }
      }
    };

    // 监听设备旋转
    window.addEventListener('orientationchange', handleOrientationChange);
    // 备用方案：监听窗口尺寸变化
    window.addEventListener('resize', () => {
      const isLandscape = window.innerWidth > window.innerHeight;
      setOrientation(isLandscape ? 'landscape' : 'portrait');
    });

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [playingVideo, isFullscreen]);

  // 进入全屏
  const enterFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.mozRequestFullScreen) {
        videoRef.current.mozRequestFullScreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
      setIsFullscreen(true);
    }
  };

  // 退出全屏
  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
    setIsFullscreen(false);
  };

  // 切换全屏
  const toggleFullscreen = () => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  // 监听全屏状态变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4 border-4 border-blue-600 border-t-transparent rounded-full" />
          <p className="text-gray-600 text-sm">正在加载精彩内容...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center bg-white p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-800 font-medium mb-2">加载失败</p>
          <p className="text-gray-600 text-sm mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-[calc(100vh-64px)] overflow-y-auto snap-y snap-mandatory scroll-smooth"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    >
      {/* First Section - Hero with horizontal scroll categories */}
      <div className="h-[calc(100vh-64px)] snap-start relative bg-white flex flex-col">
        {/* Banner Carousel Area - Full width from top (45% of viewport) */}
        <div className="relative w-full flex-shrink-0" style={{ height: 'calc((100vh - 64px) * 0.45)' }}>
          <div
            ref={bannerViewportRef}
            data-testid="home-banner"
            className="relative h-full overflow-hidden"
            style={{ touchAction: 'pan-y' }}
            onTouchStart={handleBannerTouchStart}
            onTouchMove={handleBannerTouchMove}
            onTouchEnd={handleBannerTouchEnd}
            onTouchCancel={handleBannerTouchCancel}
          >
            {/* Banner Images */}
            <div className="relative h-full">
              {/* 预加载每一张图：大图还没下载完时不参与轮播，避免移动端出现空白页。 */}
              <div className="hidden" aria-hidden="true">
                {bannerData.map((banner) => (
                  <img
                    key={`preload-${banner.id}`}
                    src={banner.image}
                    alt=""
                    onLoad={() => markBannerLoaded(banner.id)}
                    onError={() => markBannerFailed(banner.id)}
                  />
                ))}
              </div>
              {bannerData.length === 0 ? (
                <div className="w-full h-full bg-white flex flex-col items-center justify-center">
                  <img src="/images/askfirst-logo.png" alt="先问 ASKFIRST" className="h-44 w-72 object-contain rounded-lg mb-4 invert" />
                  <p className="text-sm text-gray-500">首页轮播尚未配置</p>
                </div>
              ) : visibleBanners.length === 0 ? (
                <div className="w-full h-full bg-white flex flex-col items-center justify-center">
                  <img src="/images/askfirst-logo.png" alt="先问 ASKFIRST" className="h-28 w-44 object-contain mb-3 invert" />
                  <p className="text-sm text-gray-500">正在加载轮播图片…</p>
                </div>
              ) : visibleBanners.map((banner, index) => {
                const isUserSliding = isBannerGestureActive || isBannerSettling;
                const relativePosition = getBannerRelativePosition(index);
                const slideStyle = isUserSliding
                  ? {
                      opacity: Math.abs(relativePosition) <= 1 ? 1 : 0,
                      transform: `translate3d(calc(${relativePosition * 100}% + ${bannerDragX}px), 0, 0)`,
                      transition: isBannerSettling
                        ? 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)'
                        : 'none',
                      willChange: 'transform'
                    }
                  : { opacity: index === currentBanner ? 1 : 0 };

                return (
                <div
                  key={banner.id}
                  data-testid={`home-banner-slide-${index}`}
                  className={`absolute inset-0 ${isUserSliding ? '' : 'transition-opacity duration-1000'}`}
                  style={slideStyle}
                >
                  {banner.isMinimal ? (
                    // 极简大留白样式
                    <div className="w-full h-full bg-white flex flex-col items-center justify-center">
                      <h1 className="text-3xl font-bold tracking-[0.3em] text-gray-900 mb-6">
                        {banner.title}
                      </h1>
                      <img 
                        src="/images/askfirst-logo.png"
                        alt="先问 ASKFIRST"
                        className="h-44 w-72 object-contain rounded-lg invert"
                      />
                    </div>
                  ) : (
                    // 普通图片样式
                    <>
                      <img 
                        src={banner.image}
                        alt={banner.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20" />
                    </>
                  )}
                </div>
                );
              })}
            </div>
            
            {/* Banner Indicators */}
            <div className="absolute bottom-4 right-6 flex gap-2">
              {visibleBanners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    pauseBannerAutoPlay();
                    setCurrentBanner(index);
                    resumeBannerAutoPlayAfterDelay();
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentBanner ? 'bg-white w-6' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
            
          </div>
        </div>

        {/* Fixed spacing between Banner and Horizontal Scroll */}
        <div className="h-3 flex-shrink-0" />
        
        {/* Main Content Area with Horizontal Scroll (32% of viewport) */}
        <div className="flex-shrink-0" style={{ height: 'calc((100vh - 64px) * 0.32)' }}>
          {/* Horizontal Scrollable Categories - 2.5 visible */}
          <div 
            ref={scrollRef}
            data-testid="home-category-strip"
            className="flex gap-1 overflow-x-auto overflow-y-hidden scrollbar-hide"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              height: '100%',
              touchAction: 'pan-x',
              overscrollBehaviorX: 'contain',
              overscrollBehaviorY: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {videoCategories.length === 0 && (
              <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">
                首页分类尚未配置
              </div>
            )}
            {videoCategories.map((category) => (
              <div
                key={category.id}
                className="flex-shrink-0 relative cursor-pointer"
                style={{
                  width: 'calc(100vw * 0.4)',
                  minWidth: '160px',
                  height: '100%'
                }}
                onClick={() => handleCategoryClick(category.id)}
              >
                <div className={`h-full relative overflow-hidden rounded-sm ${
                  pressedCategoryId === category.id ? 'animate-category-card-tap' : ''
                }`} data-testid={`home-category-card-${category.id}`}>
                  {category.image ? (
                    <img
                      src={category.image}
                      alt={category.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full bg-gradient-to-br from-amber-400 via-rose-500 to-violet-700"
                      role="img"
                      aria-label={`${category.title}暂未配置封面`}
                    />
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent">
                    <div className="p-3 pb-4 flex items-center justify-between">
                      <p className="text-white text-sm font-medium">{category.title}</p>
                      <ChevronRight className="w-4 h-4 text-white/80" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fixed spacing between Horizontal Scroll and Bottom Image */}
        <div className="h-3 flex-shrink-0" />

        {/* Bottom banner managed by CMS */}
        <div className="relative flex-1 bg-gradient-to-br from-neutral-800 to-black overflow-hidden">
          {bottomBanner?.image ? (
            <>
              <img
                src={bottomBanner.image}
                alt={bottomBanner.buttonText || '首页底部横幅'}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {bottomBanner.showOverlay !== false && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
              <p className="text-2xl tracking-[0.45em] text-white/90 mb-2">ASKFIRST</p>
              <p className="text-sm">更多影像内容，敬请期待</p>
            </div>
          )}
          {featuredVideos.length > 0 && <div className="absolute inset-0 flex items-end justify-center pb-6">
            <button 
              onClick={() => scrollToSection(1)}
              className="bg-transparent border border-white/80 text-white px-5 py-1.5 rounded-full text-xs flex items-center gap-1 hover:bg-white/10 transition-all"
            >
              {bottomBanner?.buttonText || '了解更多'}
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>}
        </div>
      </div>

      {/* Second Section - Featured Videos */}
      <div className="min-h-[calc(100vh-64px)] snap-start bg-gray-50">
        <div className="flex flex-col">
          {/* Section Header */}
          <div className="text-center py-4 bg-white border-b sticky top-0 z-10">
            <h2 className="text-lg font-bold">精选视频</h2>
            <p className="text-xs text-gray-500 mt-1">FEATURED VIDEOS</p>
          </div>

          {/* Videos List - Scrollable */}
          <div className="px-4 py-4">
            <div className="flex flex-col gap-4">
              {featuredVideos.length === 0 && (
                <div className="py-20 text-center">
                  <p className="text-gray-800 font-medium">精选视频尚未配置</p>
                  <p className="mt-2 text-sm text-gray-500">请先在 CMS 中发布首页精选内容</p>
                </div>
              )}
              {featuredVideos.map((video) => (
                <div key={video.id}>
                  {/* Video Card - Vertical Layout */}
                  <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                    {/* Top: Video Thumbnail Container */}
                    <div className="relative w-full aspect-[16/9]">
                      <img 
                        src={video.cover}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30 flex items-center justify-center">
                        <button 
                          onClick={() => setPlayingVideo(video.id)}
                          className="bg-white/90 rounded-full p-3 hover:bg-white hover:scale-110 transition-all shadow-lg"
                        >
                          <Play className="w-5 h-5 text-black fill-black" />
                        </button>
                      </div>
                      
                      {/* Duration Badge */}
                      <span className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </span>
                    </div>
                    
                    {/* Bottom: Title and Info Container */}
                    <div className="p-3">
                      {/* Title - First Line */}
                      <h3 className="text-sm font-medium text-gray-900 line-clamp-1 mb-2 text-left">
                        {video.title}
                      </h3>
                      
                      {/* Info - Second Line */}
                      <div className="flex items-center gap-2 text-xs text-gray-500 text-left">
                        <span className="text-gray-700">{video.category}</span>
                        <span>·</span>
                        <span>{video.role}</span>
                        <span>·</span>
                        <span>{video.publishDate}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom spacing */}
          <div className="pb-20"></div>
        </div>
      </div>

      {/* Video Player Modal - 微信小程序原生video组件 */}
      {playingVideo && (
        <div className={`fixed inset-0 bg-black z-50 ${orientation === 'landscape' ? 'landscape-mode' : ''}`}>
          {/* Video Controls Overlay */}
          <div className="absolute inset-0 flex flex-col">
            {/* Top Bar */}
            <div className="absolute top-0 left-0 right-0 flex justify-between items-center p-4 z-50 bg-gradient-to-b from-black/50 to-transparent">
              <div className="text-white">
                <h3 className="text-sm font-medium">
                  {featuredVideos.find(v => v.id === playingVideo)?.title}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {/* Fullscreen Toggle Button */}
                <button 
                  onClick={toggleFullscreen}
                  className="text-white p-2 hover:bg-white/10 rounded-full transition-colors"
                  title={isFullscreen ? "退出全屏" : "进入全屏"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5" />
                  ) : (
                    <Maximize className="w-5 h-5" />
                  )}
                </button>
                
                {/* Rotate Button (for mobile devices) */}
                <button 
                  onClick={() => {
                    // 提示用户旋转设备
                    if (orientation === 'portrait') {
                      alert('请旋转设备以横屏观看');
                    } else {
                      alert('请旋转设备以竖屏观看');
                    }
                  }}
                  className="text-white p-2 hover:bg-white/10 rounded-full transition-colors md:hidden"
                  title="旋转屏幕"
                >
                  <RotateCw className="w-5 h-5" />
                </button>
                
                {/* Close Button */}
                <button 
                  onClick={() => {
                    setPlayingVideo(null);
                    if (isFullscreen) {
                      exitFullscreen();
                    }
                  }}
                  className="text-white p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Video Player */}
            <div className="flex-1 flex items-center justify-center" ref={videoRef}>
              <video
                className={`${
                  featuredVideos.find(v => v.id === playingVideo)?.isVertical 
                    ? 'h-full w-auto max-w-full' // 竖版视频：高度优先
                    : (orientation === 'landscape' || isFullscreen 
                        ? 'w-full h-full' 
                        : 'w-full h-auto max-h-full') // 横版视频：宽度优先
                } object-contain`}
                src={featuredVideos.find(v => v.id === playingVideo)?.videoUrl}
                controls
                autoPlay
                playsInline
                webkit-playsinline="true"
                x5-playsinline="true"
                style={{ backgroundColor: 'black' }}
                onEnded={() => {
                  console.log('Video ended');
                  if (isFullscreen) {
                    exitFullscreen();
                  }
                }}
              >
                您的浏览器不支持视频播放
              </video>
            </div>
            
            {/* Bottom Info Bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-4 text-sm">
                  <span>{featuredVideos.find(v => v.id === playingVideo)?.views}次观看</span>
                  <span>{featuredVideos.find(v => v.id === playingVideo)?.duration}</span>
                </div>
                <div className="text-xs text-white/60">
                  {featuredVideos.find(v => v.id === playingVideo)?.isVertical && '竖版视频 · '}
                  {orientation === 'landscape' ? '横屏模式' : '竖屏模式'}
                </div>
              </div>
            </div>
          </div>
          
          {/* 微信小程序实际代码应该是：
          <video
            id="myVideo"
            src={videoUrl}
            autoplay
            show-fullscreen-btn
            show-play-btn
            show-center-play-btn
            show-progress
            controls
            enable-auto-rotation
            page-gesture
            className="video-player"
          /> */}
        </div>
      )}

    </div>
  );
};

export default MiniAppHomePage;
